import React, { useState } from 'react';
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import { ChevronRight } from 'lucide-react';
import { NameEmailAvatar } from '../../../../../ui/components/Common';
import {
  ContextBasedGeneratorJob,
  QuestionGeneratorJob,
  TopicBasedGeneratorJob,
} from '../../../../../utils/types/content_manager_types';
import { TriageRow } from '../triage_row/triage_row';
import { DocumentDetails } from './document_details/document_details';

export interface DocumentProps {
  job: TopicBasedGeneratorJob | ContextBasedGeneratorJob;
  updateJob: (newJobData: QuestionGeneratorJob) => void;
}

export const Document = ({ job, updateJob }: DocumentProps) => {
  const [open, setOpen] = useState(false);
  const hasDetails =
    ('transcript' in job && job.transcript && job.transcript.length > 0) ||
    (job.topic && job.topic.length > 0) ||
    Boolean(job.questions?.length);

  const uploaderName =
    [job.user?.firstName, job.user?.lastName].filter(Boolean).join(' ') ||
    'Unknown user';
  const status = job.status.split('.').join('').trim();
  const statusKey = status.toLowerCase();

  const getStatusChipStyles = () => {
    if (statusKey.includes('completed')) {
      return {
        bgcolor: '#ecfdf5',
        color: '#047857',
        borderColor: '#a7f3d0',
      };
    }
    if (statusKey.includes('queue')) {
      return {
        bgcolor: '#eff6ff',
        color: '#1d4ed8',
        borderColor: '#bfdbfe',
      };
    }
    if (statusKey.includes('error')) {
      return {
        bgcolor: '#fef2f2',
        color: '#dc2626',
        borderColor: '#fecaca',
      };
    }
    return {
      bgcolor: '#f8fafc',
      color: '#475569',
      borderColor: '#e2e8f0',
    };
  };

  const toggleDetails = () => {
    if (hasDetails) {
      setOpen(!open);
    }
  };

  const chipStyles = getStatusChipStyles();

  return (
    <>
      <Box
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: open ? '#f0fdf4' : '#ffffff',
          transition: 'all 0.15s ease',
          '&:hover': {
            bgcolor: open ? '#f0fdf4' : '#f8fafc',
          },
        }}
      >
        <TriageRow>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, gap: 1 }}>
            <IconButton
              size='small'
              onClick={toggleDetails}
              disabled={!hasDetails}
              aria-label={
                open ? 'Collapse document details' : 'Expand document details'
              }
              sx={{
                width: 28,
                height: 28,
                bgcolor: '#f8fafc',
                border: '1px solid #dbe4ef',
                color: '#2563eb',
                transform: open ? 'rotate(90deg)' : 'none',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  bgcolor: '#eff6ff',
                },
                '&.Mui-disabled': {
                  color: '#94a3b8',
                  opacity: 0.65,
                },
              }}
            >
              <ChevronRight size={16} />
            </IconButton>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant='body2'
                fontWeight={700}
                color='text.primary'
                noWrap
              >
                {job.title}
              </Typography>
              <Stack
                direction='row'
                spacing={0.75}
                alignItems='center'
                flexWrap='wrap'
                sx={{ mt: 0.25 }}
              >
                <Typography variant='caption' color='text.secondary'>
                  {job.generationType || 'context'}
                </Typography>
                {'filename' in job && job.filename && (
                  <>
                    <Typography variant='caption' color='text.disabled'>
                      •
                    </Typography>
                    <Typography
                      variant='caption'
                      color='text.secondary'
                      noWrap
                      sx={{ maxWidth: 220 }}
                    >
                      {job.filename}
                    </Typography>
                  </>
                )}
                <Typography variant='caption' color='text.disabled'>
                  •
                </Typography>
                <Typography variant='caption' color='text.secondary'>
                  {job.questions?.length || 0} questions
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Typography variant='caption' color='text.secondary'>
            {job.creationDateTime !== 0
              ? new Date(job.creationDateTime * 1000).toLocaleString()
              : 'Unavailable'}
          </Typography>

          <Box>
            <Chip
              label={status}
              size='small'
              variant='outlined'
              sx={{
                fontSize: '0.6875rem',
                fontWeight: 700,
                ...chipStyles,
              }}
            />
          </Box>

          <Box>
            <NameEmailAvatar
              name={uploaderName}
              email={job.user?.email}
              avatar={undefined}
              avatarWidth='30px'
              avatarHeight='30px'
              nameFontSize='13px'
              emailFontSize='11px'
            />
          </Box>
        </TriageRow>
      </Box>
      {open && <DocumentDetails job={job} updateJob={updateJob} />}
    </>
  );
};
